import { lensReaderStorageKey } from './lensReaderStorage';

/**
 * What this browser remembers about the dashboards this reader has already opened.
 *
 * Three separate return-visit questions are answered from one record, because all three are
 * answered by the same fact, the state of a board the last time this person had it on screen:
 *
 *  - Has this board changed since I last looked at it? (`version`, `updatedAt`)
 *  - Which boards did I actually use recently? (`seenAt`)
 *  - Can I get back to the board with the filters I left on? (`view`)
 *
 * It is deliberately local. A visit is one person's reading history on one machine, so writing it
 * to the reporting service would make a private habit into shared dashboard state and would put a
 * write on the path of simply opening a board. The cost is that the memory does not follow someone
 * to another browser, which is stated in the copy rather than hidden: the offer to restore only
 * appears where the memory exists.
 *
 * Every read and every write is guarded. Private browsing, a managed browser with site data
 * blocked, and a full quota all throw here, and none of them may cost a reader the board.
 */

/** A remembered board state is a convenience, never a source of numbers, so the shape is versioned. */
export const LENS_VISITS_STORAGE_KEY = 'askRichard:lens-visits:v1';

/** Same-tab notification. The cross-tab signal is the browser's own `storage` event. */
export const LENS_VISITS_CHANGED_EVENT = 'askrichard:lens-visits-changed';

/**
 * Boards remembered at once. A reader with a long history does not need a fourth page of it, and
 * an unbounded map is the thing that eventually makes `setItem` throw for everything else in the
 * workspace. Oldest visit is dropped first.
 */
export const LENS_VISITS_LIMIT = 40;

/**
 * Longest remembered board state. A cross-filter selection over a wide dimension can encode to
 * kilobytes, and a filter set that large is not something a reader recognises as "the filters I
 * left on" anyway. Over the cap the visit is still remembered; only its board state is dropped.
 */
export const LENS_VIEW_MEMORY_LIMIT = 4000;

export type LensVisit = {
  /** The board's spec version when the reader last had it open. */
  version: number;
  /** The board's own last-changed time at that moment, in ISO form. */
  updatedAt: string;
  /** When this reader last opened it, in ISO form. */
  seenAt: string;
  /** The board's name at that moment, so a pinned row still has a name when the Lens list fails. */
  title?: string;
  /** The viewer-chosen board state as last left, in whatever encoding the Lens URL codec produces. */
  view?: string;
};

export type LensVisits = Record<string, LensVisit>;

type LensLike = { id: string; title?: string; version?: number; updatedAt?: string };

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function readVisit(value: unknown): LensVisit | null {
  if (!isRecord(value)) {
    return null;
  }
  const version = typeof value.version === 'number' && Number.isFinite(value.version) ? value.version : null;
  const updatedAt = typeof value.updatedAt === 'string' ? value.updatedAt : '';
  const seenAt = typeof value.seenAt === 'string' ? value.seenAt : '';
  // A visit with no version and no time cannot answer any of the three questions, so it is not a
  // visit. Anything narrower than that is tolerated: a record written by an older build is still
  // useful for the parts it does carry.
  if (version === null && !updatedAt && !seenAt) {
    return null;
  }
  const title = typeof value.title === 'string' && value.title.trim() ? value.title.trim() : undefined;
  const view =
    typeof value.view === 'string' && value.view && value.view.length <= LENS_VIEW_MEMORY_LIMIT
      ? value.view
      : undefined;
  return {
    version: version ?? 0,
    updatedAt,
    seenAt,
    ...(title ? { title } : {}),
    ...(view ? { view } : {}),
  };
}

/** Reads the whole record, tolerantly. Anything unreadable reads as no history at all. */
export function readLensVisits(email?: string | null): LensVisits {
  const key = lensReaderStorageKey(LENS_VISITS_STORAGE_KEY, email);
  if (typeof window === 'undefined' || !key) {
    return {};
  }

  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) {
      return {};
    }
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed)) {
      return {};
    }
    const visits: LensVisits = {};
    for (const [lensId, value] of Object.entries(parsed)) {
      if (!lensId) {
        continue;
      }
      const visit = readVisit(value);
      if (visit) {
        visits[lensId] = visit;
      }
    }
    return visits;
  } catch {
    // Blocked site data, private mode, or a hand-edited value. A reader with no reading history
    // is a state the workspace already has to render, so fall into it rather than failing.
    return {};
  }
}

/** Newest visit first, oldest dropped, so the record cannot grow without limit. */
function pruned(visits: LensVisits): LensVisits {
  const entries = Object.entries(visits);
  if (entries.length <= LENS_VISITS_LIMIT) {
    return visits;
  }
  entries.sort(([, a], [, b]) => (b.seenAt ?? '').localeCompare(a.seenAt ?? ''));
  return Object.fromEntries(entries.slice(0, LENS_VISITS_LIMIT));
}

/**
 * Persists the record and tells this tab about it. Returns the record that was persisted, which is
 * the pruned one, so a caller's in-memory copy cannot drift above the cap.
 */
export function writeLensVisits(visits: LensVisits, email?: string | null): LensVisits {
  const next = pruned(visits);
  const key = lensReaderStorageKey(LENS_VISITS_STORAGE_KEY, email);
  if (typeof window === 'undefined' || !key) {
    return next;
  }

  try {
    window.localStorage.setItem(key, JSON.stringify(next));
    window.dispatchEvent(new Event(LENS_VISITS_CHANGED_EVENT));
  } catch {
    // The reader loses the memory of this visit and nothing else. Opening a board, reading it, and
    // navigating away all have to keep working in a browser that refuses to store anything.
  }
  return next;
}

/**
 * Records that the reader has this board on screen now, and at which version.
 *
 * Called on open rather than on close, because a reader closes a tab without telling anyone. The
 * consequence is deliberate: a board that changes while it is open is reported as changed on the
 * next visit, which is the visit on which the reader can do something about it.
 *
 * The board state left on the previous visit is preserved. A new visit does not by itself mean the
 * reader has abandoned their filters; only leaving the board at its defaults does, and that is
 * recorded through {@link rememberLensView}.
 */
export function recordLensOpened(lens: LensLike, now: Date = new Date(), email?: string | null): LensVisits {
  const visits = readLensVisits(email);
  const existing = visits[lens.id];
  return writeLensVisits(
    {
      ...visits,
      [lens.id]: {
        version: typeof lens.version === 'number' ? lens.version : (existing?.version ?? 0),
        updatedAt: typeof lens.updatedAt === 'string' ? lens.updatedAt : (existing?.updatedAt ?? ''),
        seenAt: now.toISOString(),
        ...(lens.title ? { title: lens.title } : existing?.title ? { title: existing.title } : {}),
        ...(existing?.view ? { view: existing.view } : {}),
      },
    },
    email,
  );
}

/**
 * Records the board state the reader has chosen, so it can be offered back tomorrow.
 *
 * `undefined` means the board is at its defaults, which is a real answer and not a missing one: it
 * clears the memory, so a reader who deliberately cleared their filters is not offered them again.
 * A board never opened is not given a visit by this alone; a filter cannot precede an open.
 */
export function rememberLensView(lensId: string, view: string | undefined, email?: string | null): LensVisits {
  const visits = readLensVisits(email);
  const existing = visits[lensId];
  if (!existing) {
    return visits;
  }
  const keep = view && view.length <= LENS_VIEW_MEMORY_LIMIT ? view : undefined;
  if (existing.view === keep) {
    return visits;
  }
  const next: LensVisit = { ...existing };
  if (keep) {
    next.view = keep;
  } else {
    delete next.view;
  }
  return writeLensVisits({ ...visits, [lensId]: next }, email);
}

/** Drops one board's history, for a board that no longer exists or a reader who says start fresh. */
export function forgetLensVisit(lensId: string, email?: string | null): LensVisits {
  const visits = readLensVisits(email);
  if (!(lensId in visits)) {
    return visits;
  }
  const next = { ...visits };
  delete next[lensId];
  return writeLensVisits(next, email);
}

export type LensChange = {
  /** The version the reader last saw. */
  seenVersion: number;
  /** When the reader last opened it, in ISO form, or '' when that was not recorded. */
  seenAt: string;
  /** The board's current last-changed time, in ISO form. */
  changedAt: string;
};

/**
 * Whether this board has changed since the reader last opened it, or null when that cannot be
 * said. Version is the test, not `updatedAt`: version is what the reporting service increments on
 * a write, so it is the one value that cannot move for a reason a reader would not call a change.
 *
 * A board with no recorded visit returns null rather than "changed". A first visit is not news,
 * and a reader who has never opened a board does not need to be told it has been edited.
 */
export function lensChangeSinceVisit(lens: LensLike, visits: LensVisits): LensChange | null {
  const visit = visits[lens.id];
  if (!visit) {
    return null;
  }
  const current = typeof lens.version === 'number' ? lens.version : null;
  if (current === null || !visit.version || current <= visit.version) {
    return null;
  }
  return {
    seenVersion: visit.version,
    seenAt: visit.seenAt,
    changedAt: typeof lens.updatedAt === 'string' ? lens.updatedAt : '',
  };
}

/** The ids of boards that have changed since the reader last opened them. */
export function changedLensIds(lenses: ReadonlyArray<LensLike>, visits: LensVisits): Set<string> {
  const changed = new Set<string>();
  for (const lens of lenses) {
    if (lensChangeSinceVisit(lens, visits)) {
      changed.add(lens.id);
    }
  }
  return changed;
}

/** The board state remembered for a board, or undefined when there is nothing to offer back. */
export function rememberedLensView(lensId: string, visits: LensVisits): string | undefined {
  return visits[lensId]?.view;
}

/**
 * The boards this reader actually used, most recent first, restricted to ones still in the list.
 *
 * A board the reader can no longer see is not offered as a way back into work. It is dropped here
 * rather than shown as an unavailable row, because the list this reads from is only trustworthy
 * when it loaded: a caller must pass a list it knows to be complete.
 */
export function recentlyOpenedLenses<T extends LensLike>(
  lenses: ReadonlyArray<T>,
  visits: LensVisits,
  limit = 5,
): Array<T> {
  return lenses
    .filter((lens) => Boolean(visits[lens.id]?.seenAt))
    .sort((a, b) => (visits[b.id]?.seenAt ?? '').localeCompare(visits[a.id]?.seenAt ?? ''))
    .slice(0, limit);
}

/**
 * Subscribes to changes from this tab and from every other tab in this browser.
 *
 * Two tabs are the ordinary case, not an edge: a reader opens a board in a second tab to compare
 * it against the first. Without the cross-tab half, the tab left in the background keeps claiming
 * a board has changed that the reader has already been to look at.
 */
export function subscribeLensVisits(listener: () => void, email?: string | null): () => void {
  const key = lensReaderStorageKey(LENS_VISITS_STORAGE_KEY, email);
  if (typeof window === 'undefined' || !key) {
    return () => {};
  }

  const onStorage = (event: StorageEvent) => {
    // A null key is a whole-store clear, which is also news.
    if (event.key === null || event.key === key) {
      listener();
    }
  };
  window.addEventListener(LENS_VISITS_CHANGED_EVENT, listener);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(LENS_VISITS_CHANGED_EVENT, listener);
    window.removeEventListener('storage', onStorage);
  };
}
